import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { Spinner } from '@nike/podium';
import LinePlanEntityCard from './LinePlanEntityCard';
import ProductCard from './ProductCard';
import { cardScaleAtom, filteredCardsAtom, groupByAtom, groupedCardsAtom } from '~/atoms/cardView';
import CardViewWithCustomGroups from './CardViewWithCustomGroups';
import { useLinePlanEntity } from '~/atoms/hooks/useLinePlanEntity';
import { usePageActions } from '~/atoms/hooks/usePageActions';
import { useGridData } from '~/atoms/hooks/useGridData';
import { toPluralEntity } from '~/utils/linePlanEntity';
import { useProductImages } from '~/atoms/hooks/useProductImages';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
function CardGroupSection({ groupName, cards, gridClasses, onCardClick, entityType }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const cardScale = useAtomValue(cardScaleAtom);
    return (_jsxs("div", { className: "border-b border-gray-300 dark:border-gray-600", children: [_jsxs("button", { onClick: () => setIsExpanded(!isExpanded), className: "w-full flex items-center justify-between p-4 border-t border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("h2", { className: "text-xl font-bold text-gray-900 dark:text-white", children: groupName }), _jsxs("span", { className: "px-2 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm", children: [cards.length, " ", cards.length === 1 ? 'item' : 'items'] })] }), isExpanded ? (_jsx(CaretUp, { className: "w-6 h-6" })) : (_jsx(CaretDown, { className: "w-6 h-6" }))] }), isExpanded && (_jsx("div", { className: `${gridClasses} p-6`, children: cards.map((card) => entityType === 'project' || entityType === 'expression' ? (_jsx(LinePlanEntityCard, { card: card, entityType: entityType, onClick: () => onCardClick(card), scale: cardScale }, card.id)) : (_jsx(ProductCard, { card: card, onClick: () => onCardClick(card), scale: cardScale }, card.id))) }))] }));
}
function CardView() {
    const filteredData = useAtomValue(filteredCardsAtom);
    const cardScale = useAtomValue(cardScaleAtom);
    const groupBy = useAtomValue(groupByAtom);
    const groupedData = useAtomValue(groupedCardsAtom);
    const { linePlanEntity } = useLinePlanEntity();
    const { setDetailSidebar } = usePageActions();
    const { gridData, gridDataLoadingState } = useGridData();
    const { fetchImagesForModels } = useProductImages();
    const fetchedModelsRef = useRef(new Set());
    useEffect(() => {
        if (filteredData.length > 0) {
            const modelIds = filteredData
                .map((card) => {
                if ('modelId' in card) {
                    return card.modelId;
                }
                return null;
            })
                .filter((id) => id !== null);
            const newModelIds = modelIds.filter((id) => !fetchedModelsRef.current.has(id));
            if (newModelIds.length > 0) {
                newModelIds.forEach((id) => fetchedModelsRef.current.add(id));
                fetchImagesForModels(newModelIds);
            }
        }
    }, [filteredData.length, fetchImagesForModels]);
    const handleCardClick = (card) => {
        if (!gridData)
            return;
        const originalEntity = gridData.find((item) => {
            if ('modelId' in item &&
                typeof item.modelId === 'number' &&
                !('styleNumber' in item)) {
                return String(item.modelId) === card.id;
            }
            if ('styleNumber' in item && typeof item.styleNumber === 'string') {
                return item.styleNumber === card.id;
            }
            if ('projectName' in item &&
                'id' in item &&
                typeof item.id === 'string') {
                return item.id === card.id;
            }
            if ('expressionName' in item &&
                'id' in item &&
                typeof item.id === 'string') {
                return item.id === card.id;
            }
            return false;
        });
        if (originalEntity) {
            setDetailSidebar({
                isOpen: true,
                entity: originalEntity,
                entityType: linePlanEntity
            });
        }
    };
    const getGridClasses = () => {
        switch (cardScale) {
            case 'small':
                return 'grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(200px,100%),1fr))]';
            case 'large':
                return 'grid gap-8 grid-cols-[repeat(auto-fill,minmax(min(360px,100%),1fr))]';
            case 'medium':
            default:
                return 'grid gap-6 grid-cols-[repeat(auto-fill,minmax(min(280px,100%),1fr))]';
        }
    };
    if (gridDataLoadingState === 'loading') {
        return (_jsx("div", { className: "flex items-center justify-center h-96", children: _jsx(Spinner, { size: "large" }) }));
    }
    if (gridDataLoadingState === 'hasError') {
        return (_jsx("div", { className: "flex items-center justify-center h-96", children: _jsxs("div", { className: "text-center", children: [_jsxs("p", { className: "text-xl pds-color-text-secondary mb-2", children: ["Failed to load ", toPluralEntity(linePlanEntity)] }), _jsx("p", { className: "text-sm pds-color-text-secondary", children: "Please try refreshing the page" })] }) }));
    }
    if (gridDataLoadingState === 'hasData' && filteredData.length === 0) {
        return (_jsx("div", { className: "flex items-center justify-center h-96", children: _jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-xl pds-color-text-secondary mb-2", children: `No ${toPluralEntity(linePlanEntity)} found` }), _jsx("p", { className: "text-sm pds-color-text-secondary", children: "Try adjusting your filters or search term" })] }) }));
    }
    if (groupBy === 'custom') {
        return _jsx(CardViewWithCustomGroups, {});
    }
    if (groupBy !== 'none') {
        return (_jsx("div", { className: "w-full px-6", children: _jsx("div", { className: "py-6 space-y-6 max-w-[1440px] mx-auto", children: Object.entries(groupedData).map(([groupName, cards]) => (_jsx(CardGroupSection, { groupName: groupName, cards: cards, gridClasses: getGridClasses(), onCardClick: handleCardClick, entityType: linePlanEntity }, groupName))) }) }));
    }
    return (_jsx("div", { className: "w-full px-6", children: _jsx("div", { className: `${getGridClasses()} py-6 max-w-[1440px] mx-auto`, children: filteredData.map((card) => linePlanEntity === 'project' || linePlanEntity === 'expression' ? (_jsx(LinePlanEntityCard, { card: card, entityType: linePlanEntity, onClick: () => handleCardClick(card), scale: cardScale }, card.id)) : (_jsx(ProductCard, { card: card, onClick: () => handleCardClick(card), scale: cardScale }, card.id))) }) }));
}
export default CardView;
